import React, { useMemo } from 'react';
import SectionCard from '../components/SectionCard';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtKg = (n) => fmtNum(n, 1);

const PALETTE = ['#0F3460', '#FC536D', '#7BA7D4', '#A9B4C4', '#0E9F6E', '#E9A13B'];

// Sección: Detalle comparativo por máquina — cruza lotes (recepción) contra
// ciclos de lavado (válidos y abortados), máquina por máquina, para ubicar
// fácilmente dónde está la diferencia del cuadre general.
const DetalleComparativoSection = ({ participacion }) => {
  const { data, loading, error, reload } = participacion;

  const computed = useMemo(() => {
    const lotes = Array.isArray(data?.lotes) ? data.lotes : [];
    const cycles = Array.isArray(data?.cycles) ? data.cycles : [];

    const machines = new Map();
    const ensure = (id, nombre) => {
      if (!machines.has(id)) {
        machines.set(id, {
          id,
          nombre: nombre || 'Sin máquina',
          lotes: 0,
          kgLotes: 0,
          ciclosMaquina: 0,
          kgCiclosMaquina: 0,
          abortados: 0,
          kgAbortadosNominal: 0,
        });
      }
      return machines.get(id);
    };

    lotes.forEach((l) => {
      const id = l?.maquina?.id || 'sin-maquina';
      const m = ensure(id, l?.maquina?.nombre);
      m.lotes += 1;
      m.kgLotes += Number(l?.pesoKg || 0);
    });

    cycles.forEach((c) => {
      const id = c?.maquina?.id || 'sin-maquina';
      const m = ensure(id, c?.maquina?.nombre);
      if (c?.abortado === true) {
        // El registro de abortado es una referencia manual del contador: su
        // `ciclos` no se suma sobre el total de la máquina, se resta (mismo
        // criterio que en los KPIs de arriba).
        m.abortados += Number(c.ciclos || 0);
        m.kgAbortadosNominal += Number(c.ciclos || 0) * Number(c.capacidad || 0);
      } else {
        m.ciclosMaquina += Number(c.ciclos || 0);
        m.kgCiclosMaquina += Number(c.kilosLavados || 0);
      }
    });

    // "Ciclos" en esta tabla = ciclos VÁLIDOS (ciclos de máquina - abortados),
    // para que la diferencia total coincida con la de las tarjetas de arriba.
    const rows = Array.from(machines.values())
      .map((m) => {
        const ciclos = Math.max(m.ciclosMaquina - m.abortados, 0);
        const kgCiclos = Math.max(m.kgCiclosMaquina - m.kgAbortadosNominal, 0);
        return {
          ...m,
          ciclos,
          kgCiclos,
          difCantidad: ciclos - m.lotes,
          difKg: kgCiclos - m.kgLotes,
        };
      })
      .sort((a, b) => b.lotes - a.lotes);

    const totals = rows.reduce(
      (acc, r) => ({
        lotes: acc.lotes + r.lotes,
        ciclosMaquina: acc.ciclosMaquina + r.ciclosMaquina,
        ciclos: acc.ciclos + r.ciclos,
        abortados: acc.abortados + r.abortados,
        kgLotes: acc.kgLotes + r.kgLotes,
        kgCiclos: acc.kgCiclos + r.kgCiclos,
      }),
      { lotes: 0, ciclosMaquina: 0, ciclos: 0, abortados: 0, kgLotes: 0, kgCiclos: 0 },
    );
    const maxLotes = Math.max(...rows.map((r) => r.lotes), 1);
    const maxCiclos = Math.max(...rows.map((r) => r.ciclos), 1);

    return { rows, totals, maxLotes, maxCiclos };
  }, [data]);

  return (
    <SectionCard
      title="Detalle comparativo por máquina"
      hint="Diferencia de cantidad y de kilos, máquina por máquina."
      loading={loading}
      error={error}
      onReload={reload}
    >
      {!loading && computed.rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Sin datos en el periodo seleccionado.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 pl-1 pb-2">Máquina</th>
                <th className="text-center text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Lotes vs. Ciclos</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Lotes</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Ciclos máquina</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Abortados</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Ciclos validados</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Dif.</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Kg lotes</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Kg ciclos</th>
                <th className="text-right text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 px-3 pb-2">Dif. kg</th>
              </tr>
            </thead>
            <tbody>
              {computed.rows.map((r, i) => {
                const color = PALETTE[i % PALETTE.length];
                return (
                  <tr key={r.id}>
                    <td className="text-xs font-semibold py-2.5 pl-1 text-[#1A1C1C] dark:text-white whitespace-nowrap">{r.nombre}</td>
                    <td className="py-2.5 px-3 w-[190px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9.5px] font-semibold text-[#43474F] dark:text-gray-400 w-6">LOT</span>
                        <span className="flex-1 h-[9px] bg-[#EEEEEE] dark:bg-gray-700 rounded-full overflow-hidden">
                          <span className="block h-full rounded-full" style={{ width: `${(r.lotes / computed.maxLotes) * 100}%`, background: color }} />
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9.5px] font-semibold text-[#43474F] dark:text-gray-400 w-6">CIC</span>
                        <span className="flex-1 h-[9px] bg-[#EEEEEE] dark:bg-gray-700 rounded-full overflow-hidden">
                          <span className="block h-full rounded-full opacity-45" style={{ width: `${(r.ciclos / computed.maxCiclos) * 100}%`, background: color }} />
                        </span>
                      </div>
                    </td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums">{fmtNum(r.lotes)}</td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums">{fmtNum(r.ciclosMaquina)}</td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums text-[#B71D3F]">{fmtNum(r.abortados)}</td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums font-semibold">{fmtNum(r.ciclos)}</td>
                    <td className={`text-right text-[13.5px] font-bold py-2.5 px-3 tabular-nums ${r.difCantidad === 0 ? 'text-[#0E9F6E]' : 'text-[#B71D3F]'}`}>
                      {r.difCantidad > 0 ? '+' : ''}{fmtNum(r.difCantidad)}
                    </td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums">{fmtKg(r.kgLotes)}</td>
                    <td className="text-right text-[13.5px] py-2.5 px-3 tabular-nums">{fmtKg(r.kgCiclos)}</td>
                    <td className={`text-right text-[13.5px] font-bold py-2.5 px-3 tabular-nums ${Math.abs(r.difKg) < 1 ? 'text-[#0E9F6E]' : r.difKg > 0 ? 'text-[#0E9F6E]' : 'text-[#B71D3F]'}`}>
                      {r.difKg > 0 ? '+' : ''}{fmtKg(r.difKg)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#001F41]">
                <td className="text-xs font-bold py-3 pl-1 rounded-l-lg text-white">TOTAL</td>
                <td></td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-white">{fmtNum(computed.totals.lotes)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-white">{fmtNum(computed.totals.ciclosMaquina)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-[#FC536D]">{fmtNum(computed.totals.abortados)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-white">{fmtNum(computed.totals.ciclos)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-[#FC536D]">
                  {(computed.totals.ciclos - computed.totals.lotes) > 0 ? '+' : ''}{fmtNum(computed.totals.ciclos - computed.totals.lotes)}
                </td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-white">{fmtKg(computed.totals.kgLotes)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 tabular-nums text-white">{fmtKg(computed.totals.kgCiclos)}</td>
                <td className="text-right text-[13.5px] font-bold py-3 px-3 rounded-r-lg tabular-nums text-[#7FE3BC]">
                  {(computed.totals.kgCiclos - computed.totals.kgLotes) > 0 ? '+' : ''}{fmtKg(computed.totals.kgCiclos - computed.totals.kgLotes)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

export default DetalleComparativoSection;
